import React, { useState, useRef } from 'react';
import { X, Upload, MapPin, Home, Building2, Trees, IndianRupee, Phone, Mail, User, Check, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';

const ListPropertyModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    owner_name: '',
    email: '',
    phone: '',
    property_type: 'residential',
    property_subtype: 'apartment',
    title: '',
    description: '',
    location: '',
    city: '',
    area_sqft: '',
    bedrooms: '',
    bathrooms: '',
    price: '',
    listing_type: 'sale',
    amenities: ''
  });

  const propertySubtypes = {
    residential: [
      { value: 'apartment', label: 'Apartment' },
      { value: 'villa', label: 'Villa / Independent House' },
      { value: 'plot', label: 'Plot / Land' },
      { value: 'penthouse', label: 'Penthouse' },
      { value: 'builder_floor', label: 'Builder Floor' },
    ],
    commercial: [
      { value: 'office', label: 'Office Space' },
      { value: 'shop', label: 'Shop / Showroom' },
      { value: 'warehouse', label: 'Warehouse / Godown' },
      { value: 'co_working', label: 'Co-working Space' },
    ],
    agricultural: [
      { value: 'farmland', label: 'Farm Land' },
      { value: 'farmhouse', label: 'Farm House' },
      { value: 'agricultural_plot', label: 'Agricultural Plot' },
    ]
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, { file, preview: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      images.forEach((img, idx) => {
        submitData.append('images', img.file);
      });

      await apiClient.post('/properties/list-request', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('Property listing request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit listing request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      owner_name: '',
      email: '',
      phone: '',
      property_type: 'residential',
      property_subtype: 'apartment',
      title: '',
      description: '',
      location: '',
      city: '',
      area_sqft: '',
      bedrooms: '',
      bathrooms: '',
      price: '',
      listing_type: 'sale',
      amenities: ''
    });
    setImages([]);
    setSubmitted(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="list-property-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">List Your Property</h2>
            <p className="text-sm text-gray-500 mt-1">Our team will review and get in touch within 24 hours</p>
          </div>
          <button 
            onClick={() => { onClose(); resetForm(); }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Request Submitted!</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                Thank you for your interest in listing with ZenStudios. Our team will review your property details and contact you within 24-48 hours.
              </p>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => { onClose(); resetForm(); }} className="rounded-full">
                  Close
                </Button>
                <Button onClick={resetForm} className="rounded-full bg-primary">
                  List Another Property
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Owner Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" /> Owner Details
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      placeholder="John Doe"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Property Type */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" /> Property Type
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Select 
                      value={formData.property_type} 
                      onValueChange={(value) => setFormData({
                        ...formData, 
                        property_type: value,
                        property_subtype: propertySubtypes[value][0].value
                      })}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential"><Home className="w-4 h-4 inline mr-2" />Residential</SelectItem>
                        <SelectItem value="commercial"><Building2 className="w-4 h-4 inline mr-2" />Commercial</SelectItem>
                        <SelectItem value="agricultural"><Trees className="w-4 h-4 inline mr-2" />Agricultural</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sub-type *</Label>
                    <Select 
                      value={formData.property_subtype} 
                      onValueChange={(value) => setFormData({...formData, property_subtype: value})}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {propertySubtypes[formData.property_type]?.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Listing Type *</Label>
                    <Select 
                      value={formData.listing_type} 
                      onValueChange={(value) => setFormData({...formData, listing_type: value})}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sale">For Sale</SelectItem>
                        <SelectItem value="rent">For Rent</SelectItem>
                        <SelectItem value="lease">For Lease</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" /> Property Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Property Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="e.g., Luxurious 3BHK Apartment in Jubilee Hills"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Location / Address *</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="e.g., Road No. 10, Jubilee Hills"
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="e.g., Hyderabad"
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <Label>Area (sq.ft) *</Label>
                      <Input
                        type="number"
                        value={formData.area_sqft}
                        onChange={(e) => setFormData({...formData, area_sqft: e.target.value})}
                        placeholder="1500"
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                    {formData.property_type === 'residential' && (
                      <>
                        <div>
                          <Label>Bedrooms</Label>
                          <Select 
                            value={formData.bedrooms} 
                            onValueChange={(value) => setFormData({...formData, bedrooms: value})}
                          >
                            <SelectTrigger className="rounded-xl mt-1">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {['1', '2', '3', '4', '5', '6+'].map(num => (
                                <SelectItem key={num} value={num}>{num} BHK</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Bathrooms</Label>
                          <Select 
                            value={formData.bathrooms} 
                            onValueChange={(value) => setFormData({...formData, bathrooms: value})}
                          >
                            <SelectTrigger className="rounded-xl mt-1">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {['1', '2', '3', '4', '5+'].map(num => (
                                <SelectItem key={num} value={num}>{num}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    <div>
                      <Label>Expected Price (₹) *</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="5000000"
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Describe your property - features, nearby landmarks, amenities..."
                      rows={4}
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Amenities (comma separated)</Label>
                    <Input
                      value={formData.amenities}
                      onChange={(e) => setFormData({...formData, amenities: e.target.value})}
                      placeholder="e.g., Swimming Pool, Gym, Club House, Power Backup"
                      className="rounded-xl mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-primary" /> Property Images
                </h3>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-1">Click to upload images</p>
                  <p className="text-xs text-gray-400">PNG, JPG, WEBP up to 5MB each (max 10 images)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>
                
                {images.length > 0 && (
                  <div className="grid grid-cols-5 gap-3 mt-4">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img 
                          src={img.preview} 
                          alt={`Property ${idx + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { onClose(); resetForm(); }}
                  className="rounded-full flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="rounded-full flex-1 bg-primary"
                  data-testid="submit-listing-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Listing Request'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListPropertyModal;
